import { createMemo, createSignal, Show } from "solid-js"
import { Form } from "@/designSystem/components/form"
import { Toggle } from "@/designSystem/components/toggle"
import { RightPanel } from "@/designSystem/patterns/right-panel"
import { graphql } from "@/graphql"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { createBlogRouter } from "@/routes/posts-router"
import { useI18nContext } from "../../i18n/i18n-solid"
import { AudienceForm, type AudienceSettings } from "./_audience-form"
import { type EditorPost, usePostEditor } from "./post-editor-context"

export function PostAudience() {
  const postEditor = usePostEditor()
  const { LL } = useI18nContext()

  return (
    <Show
      when={!postEditor.isLoading() && postEditor.post()}
      fallback={<div>{LL().common.loading()}</div>}
    >
      {post => <SafePostAudience post={post()} />}
    </Show>
  )
}

function SafePostAudience(props: { post: EditorPost }) {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const [currentAudience, setCurrentAudience] = createSignal<AudienceSettings>({
    private: props.post.private ?? false,
    dirty: (props.post.tiers?.length ?? 0) > 0,
  })

  const [updatePostSettingsState, updatePostSettings] =
    createMutationWithErrorHandling(
      graphql(
        `mutation UpdatePostAudience($id: ID!, $input: UpdatePostSettingsInput!) {
          updatePostSettings(id: $id, input: $input) {
            ... on MutationUpdatePostSettingsSuccess {
              data {
                id
                private
                tiers {
                  id
                  name
                }
              }
            }
            ...ErrorFragment
          }
        }`,
        [ErrorFragment],
      ),
      "updatePostSettings",
    )

  const [updateCommentsState, updateComments] = createMutationWithErrorHandling(
    graphql(
      `mutation UpdatePostCommentsEnabled($id: ID!, $input: UpdatePostSettingsInput!) {
          updatePostSettings(id: $id, input: $input) {
            ... on MutationUpdatePostSettingsSuccess {
              data {
                id
                commentsEnabled
              }
            }
            ...ErrorFragment
          }
        }`,
      [ErrorFragment],
    ),
    "updatePostSettings",
  )

  const [formCommentsEnabled, setFormCommentsEnabled] = createSignal(
    props.post.commentsEnabled ?? true,
  )
  const commentsEnabledDirty = createMemo(
    () => formCommentsEnabled() !== (props.post.commentsEnabled ?? true),
  )

  async function onSubmitAudience() {
    const audience = currentAudience()
    await updatePostSettings({
      id: props.post.id,
      input: {
        private: audience.private,
      },
    })
    toast.success(LL().postSettings.audienceSaved())
  }

  async function onSubmitCommentsEnabled() {
    await updateComments({
      id: props.post.id,
      input: { commentsEnabled: formCommentsEnabled() },
    })
    toast.success(LL().postSettings.commentsEnabledTitle())
  }

  return (
    <RightPanel
      closeHref={paths().post().url}
      header={{
        type: "title",
        title: LL().postSettings.audienceTitle(),
      }}
    >
      <Form
        title={LL().postSettings.audienceTitle()}
        description={LL().postSettings.audienceDescription()}
        hue="amber"
        iconName="user"
        onSubmit={onSubmitAudience}
      >
        <AudienceForm post={props.post} onAudienceChange={setCurrentAudience} />
        <Form.Save
          hue="amber"
          disabled={!currentAudience().dirty}
          active={updatePostSettingsState.fetching}
        />
      </Form>
      <Form
        title={LL().postSettings.commentsEnabledTitle()}
        description={LL().postSettings.commentsEnabledDescription()}
        hue="amber"
        iconName="message-circle"
        onSubmit={onSubmitCommentsEnabled}
      >
        <Toggle
          label={LL().postSettings.commentsEnabledTitle()}
          hue="amber"
          checked={formCommentsEnabled()}
          onChange={setFormCommentsEnabled}
        />
        <Form.Save
          hue="amber"
          disabled={!commentsEnabledDirty()}
          active={updateCommentsState.fetching}
        />
      </Form>
    </RightPanel>
  )
}
